import React, {useEffect, useState} from 'react';
import {
    Box,
    Button,
    Grid,
    IconButton,
    TextField,
    Typography,
    Switch,
    FormControlLabel,
    Alert, FormHelperText
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import {v4 as uuidv4} from 'uuid';

export default function CustomAttributesForm({customData, setCustomData}) {
    const [isJsonMode, setIsJsonMode] = useState(false);
    const [jsonText, setJsonText] = useState('');
    const [jsonError, setJsonError] = useState(null);
    const [uiRows, setUiRows] = useState([]);

    // Вспомогательная функция для преобразования строк UI обратно в объект данных
    const getObjectFromRows = (rows) => {
        const obj = {};
        rows.forEach(row => {
            if (row.key && row.key.trim() !== '') {
                obj[row.key] = row.value;
            }
        });
        return obj;
    };

    // Синхронизация props (customData) -> state (uiRows/jsonText)
    useEffect(() => {
        if (!customData) return;

        // 1. Логика для JSON режима
        if (isJsonMode) {
            // В JSON режиме просто обновляем текст, если объект изменился внешне
            // Сравниваем через stringify, чтобы не обновлять, если объект тот же
            const currentJson = JSON.stringify(customData, null, 2);
            if (currentJson !== jsonText && !jsonError) {
                setJsonText(currentJson);
            }
            return;
        }

        // 2. Логика для UI режима
        // Преобразуем текущие строки в объект для сравнения
        const currentUiObj = getObjectFromRows(uiRows);

        // Сравниваем входящие данные с тем, что у нас есть
        // Используем JSON.stringify для глубокого сравнения значений
        const incomingStr = JSON.stringify(customData);
        const currentStr = JSON.stringify(currentUiObj);

        // Если данные отличаются, значит обновление пришло извне (загрузка с сервера)
        // или это первая инициализация. Обновляем UI.
        // Если данные равны (мы только что напечатали символ и обновили родителя),
        // то ничего не делаем, чтобы сохранить фокус и id строк.
        if (incomingStr !== currentStr) {
            const newRows = Object.entries(customData).map(([key, value]) => ({
                id: uuidv4(), // Генерируем новые ID только при внешнем обновлении
                key: key,
                value: String(value)
            }));

            // Важный момент: если у нас есть пустые строки (которые мы добавляем вручную),
            // они не попадают в customData. Нужно сохранить их, если обновление не радикальное.
            // Но для простоты при загрузке данных с сервера мы полностью заменяем список.
            // Чтобы не терять пустую строку при вводе в другую, проверка equality выше спасет нас,
            // так как при вводе customData будет равен currentUiObj.

            // Но есть кейс: customData = {a:1}, мы добавили пустой row. currentUiObj = {a:1}.
            // incomingStr ({a:1}) === currentStr ({a:1}).
            // Мы НЕ обновляем uiRows. Пустая строка остается. Это правильное поведение.

            // Кейс инициализации: customData = {a:1}, uiRows = []. currentUiObj = {}.
            // incomingStr !== currentStr. Мы обновляем uiRows. Правильно.

            setUiRows(newRows);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customData, isJsonMode]); // jsonText и uiRows специально исключены из зависимостей

    const handleModeChange = (event) => {
        const newModeJson = event.target.checked;

        if (newModeJson) {
            // Переход UI -> JSON
            const obj = {};
            let hasEmptyKeys = false;

            uiRows.forEach(row => {
                obj[row.key] = row.value;
                if (!row.key.trim()) hasEmptyKeys = true;
            });

            setJsonText(JSON.stringify(obj, null, 2));

            if (hasEmptyKeys) {
                setJsonError("Warning: Some parameters have empty keys. They are represented as \"\".");
            } else {
                setJsonError(null);
            }
        } else {
            // Переход JSON -> UI
            try {
                const parsed = JSON.parse(jsonText);
                if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
                    const rows = Object.entries(parsed).map(([key, value]) => ({
                        id: uuidv4(),
                        key: key,
                        value: String(value)
                    }));
                    setUiRows(rows);
                    setJsonError(null);

                    // Сразу обновляем родителя
                    updateParentFromRows(rows);
                }
            } catch (e) {
                setJsonError("Invalid JSON. Fix syntax before switching back.");
            }
        }

        setIsJsonMode(newModeJson);
    };

    const handleJsonChange = (text) => {
        setJsonText(text);
        try {
            const parsed = JSON.parse(text);
            setJsonError(null);
            if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
                setCustomData(parsed);
            }
        } catch (e) {
            setJsonError("Invalid JSON syntax");
        }
    };

    const updateParentFromRows = (rows) => {
        const validObj = {};
        rows.forEach(row => {
            if (row.key && row.key.trim() !== '') {
                validObj[row.key] = row.value;
            }
        });
        setCustomData(validObj);
    };

    const handleRowChange = (id, field, value) => {
        const newRows = uiRows.map(row => {
            if (row.id === id) {
                return {...row, [field]: value};
            }
            return row;
        });
        setUiRows(newRows);
        updateParentFromRows(newRows);
    };

    const handleAddRow = () => {
        const newRows = [...uiRows, {id: uuidv4(), key: '', value: ''}];
        setUiRows(newRows);
    };

    const handleDeleteRow = (id) => {
        const newRows = uiRows.filter(row => row.id !== id);
        setUiRows(newRows);
        updateParentFromRows(newRows);
    };

    return (
        <Box>
            <Box display="flex" justifyContent="flex-start" mb={2}>
                <FormControlLabel
                    control={
                        <Switch
                            checked={isJsonMode}
                            onChange={handleModeChange}
                            color="warning"
                        />
                    }
                    label={isJsonMode ? "JSON Mode" : "UI Mode"}
                />
            </Box>

            {isJsonMode ? (
                <Box>
                    <TextField
                        fullWidth
                        multiline
                        minRows={6}
                        maxRows={12}
                        value={jsonText}
                        onChange={(e) => handleJsonChange(e.target.value)}
                        placeholder='{\n  "key": "value"\n}'
                        sx={{fontFamily: 'monospace'}}
                        error={Boolean(jsonError)}
                        helperText={jsonError}
                    />
                </Box>
            ) : (
                <Box>
                    {uiRows.length === 0 && (
                        <Typography variant="body2" color="text.secondary" sx={{mb: 2, fontStyle: 'italic'}}>
                            No custom attributes defined. Click "Add Parameter" to start.
                        </Typography>
                    )}

                    <Grid container spacing={2}>
                        {uiRows.map((row) => (
                            <Grid item size={12} key={row.id}>
                                <Grid container spacing={2}>
                                    <Grid item size={5}>
                                        <TextField
                                            fullWidth
                                            label="Key"
                                            size="small"
                                            value={row.key}
                                            error={!row.key.trim()}
                                            onChange={(e) => handleRowChange(row.id, 'key', e.target.value)}
                                        />
                                    </Grid>
                                    <Grid item size={6}>
                                        <TextField
                                            fullWidth
                                            label="Value"
                                            size="small"
                                            value={row.value}
                                            error={!row.value}
                                            onChange={(e) => handleRowChange(row.id, 'value', e.target.value)}
                                        />
                                    </Grid>
                                    <Grid item size={1} display="flex" alignItems="center" justifyContent="center">
                                        <IconButton onClick={() => handleDeleteRow(row.id)} color="error" size="small">
                                            <DeleteIcon/>
                                        </IconButton>
                                    </Grid>
                                </Grid>
                            </Grid>
                        ))}
                    </Grid>

                    <Button
                        startIcon={<AddIcon/>}
                        onClick={handleAddRow}
                        sx={{mt: 2}}
                        variant="outlined"
                    >
                        Add Parameter
                    </Button>
                </Box>
            )}
        </Box>
    );
}